<template>
    <v-card class="ma-4">
        <v-card-title>
            <v-layout row wrap>
                <v-col grow class="ma-2"> Task Templates </v-col>
                <v-btn color="primary" class="ma-2" @click="showCreateProtocolTaskTemplateForm">
                    New Task Template
                </v-btn>
            </v-layout>
        </v-card-title>
        <v-data-table
            :headers="protocolTaskTemplateHeaders"
            :items="protocolTaskTemplateItems"
            no-results-text="No tasks."
            item-key="id"
            class="elevation-1"
            :items-per-page="25"
        >
            <template v-slot:item.edit="{ item }">
                <v-icon @click="showEditProtocolTaskTemplateForm(item)">$pencil</v-icon>
            </template>
            <template v-slot:item.delete="{ item }">
                <v-icon @click="deleteProtocolTaskTemplate(item.protocolTaskTemplate)"
                    >$delete</v-icon
                >
            </template>
        </v-data-table>
        <v-dialog v-model="showProtocolTaskTemplateFormDialog" width="1000" persistent>
            <protocol-task-template-form
                v-if="showProtocolTaskTemplateFormDialog"
                :readout-groups="readoutGroups"
                :task-groups="taskGroups"
                :protocol-task-template="protocolTaskTemplateToEdit"
                :protocol="protocol"
                @closed="protocolTaskTemplateFormDialogClosed"
                @saved="savedProtocolTaskTemplate"
            />
        </v-dialog>
    </v-card>
</template>
<script>
import { defineComponent } from "vue";
import { buildProtocolTaskTemplatesAPI } from "@/services/api.services";
import { withLoadingScreen } from "@/lib/loading";
import { findReadoutGroupById, findTaskGroupById } from "@/lib/taskUtils";
import _ from "lodash";
import ProtocolTaskTemplateForm from "./ProtocolTaskTemplateForm.vue";

export default defineComponent({
    components: { ProtocolTaskTemplateForm },
    props: {
        protocol: {
            type: Object,
            required: true,
        },
        readoutGroups: {
            type: Array,
            required: true,
        },
        taskGroups: {
            type: Array,
            required: true,
        },
    },
    data() {
        return {
            protocolTaskTemplatesAPI: null,
            protocolTaskTemplates: [],
            protocolTaskTemplateItems: [],
            protocolTaskTemplateHeaders: [
                {
                    title: "Day",
                    value: "day",
                    align: "left",
                    sortable: true,
                    width: "5%",
                },
                {
                    title: "Task Group",
                    value: "taskGroupName",
                    sortable: true,
                    align: "left",
                    width: "10%",
                },
                {
                    title: "Readout Group",
                    value: "readoutGroupName",
                    sortable: true,
                    align: "left",
                    width: "12%",
                },
                {
                    title: "Edit",
                    value: "edit",
                    align: "left",
                    width: "5%",
                },
                {
                    title: "Delete",
                    value: "delete",
                    align: "left",
                    width: "5%",
                },
            ],
            showProtocolTaskTemplateFormDialog: false,
            protocolTaskTemplateToEdit: null,
        };
    },
    async mounted() {
        this.protocolTaskTemplatesAPI = buildProtocolTaskTemplatesAPI(this.protocol.code);
        await this.fetchProtocolTaskTemplates();
    },
    methods: {
        async fetchProtocolTaskTemplates() {
            await withLoadingScreen(async () => {
                this.protocolTaskTemplates = await this.protocolTaskTemplatesAPI.fetchAll();
                this.protocolTaskTemplateItems = this.protocolTaskTemplates.map(
                    (protocolTaskTemplate) => {
                        return this.mapProtocolTaskTemplate(protocolTaskTemplate);
                    }
                );
            });
        },
        mapProtocolTaskTemplate(protocolTaskTemplate) {
            const taskGroup = findTaskGroupById(protocolTaskTemplate.taskGroupId, this.taskGroups);

            if (_.isNil(taskGroup)) {
                return {
                    ...protocolTaskTemplate,
                    day: -1,
                    protocolTaskTemplate,
                    taskGroupName: "Error: missing Task Group",
                    readoutGroupName: "",
                };
            }

            const readoutGroup = findReadoutGroupById(
                protocolTaskTemplate.readoutGroupId,
                this.readoutGroups
            );

            const day = _.isNil(protocolTaskTemplate.day)
                ? taskGroup.day
                : protocolTaskTemplate.day;

            if (_.isNil(readoutGroup)) {
                return {
                    ...protocolTaskTemplate,
                    day,
                    protocolTaskTemplate,
                    taskGroupName: taskGroup.name,
                    readoutGroupName: "Error: missing Readout Group",
                };
            }

            return {
                ...protocolTaskTemplate,
                day,
                protocolTaskTemplate,
                taskGroupName: taskGroup.name,
                readoutGroupName: readoutGroup.name,
            };
        },
        showCreateProtocolTaskTemplateForm() {
            this.showProtocolTaskTemplateFormDialog = true;
            this.protocolTaskTemplateToEdit = {
                day: null,
                type: "scheduled",
                readoutGroupId: null,
                taskGroupId: null,
            };
        },
        showEditProtocolTaskTemplateForm(item) {
            this.protocolTaskTemplateToEdit = item.protocolTaskTemplate;
            this.showProtocolTaskTemplateFormDialog = true;
        },
        protocolTaskTemplateFormDialogClosed() {
            this.showProtocolTaskTemplateFormDialog = false;
            this.protocolTaskTemplateToEdit = null;
        },
        savedProtocolTaskTemplate() {
            this.protocolTaskTemplateToEdit = null;
            this.showProtocolTaskTemplateFormDialog = false;
            this.fetchProtocolTaskTemplates();
        },
        async deleteProtocolTaskTemplate(protocolTaskTemplate) {
            if (confirm("Are you sure you want to delete this task?")) {
                return withLoadingScreen(async () => {
                    await this.protocolTaskTemplatesAPI.delete(protocolTaskTemplate.id);
                    await this.fetchProtocolTaskTemplates();
                });
            }
        },
    },
});
</script>
